import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Send, MessageSquare, Mail, User, FileText } from 'lucide-react';
import MobileNav from '../components/MobileNav';
import { motion } from 'framer-motion';
import { getUser } from '../lib/auth';
import { BACKEND_URL } from '../lib/config';

const API_BASE = BACKEND_URL;

export default function ContactPage() {
  const navigate = useNavigate();
  const user = getUser();
  const isLoggedIn = !!user;
  const backTo = isLoggedIn ? '/settings' : '/login';
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to send');
      
      setSent(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col" style={{
        background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
      }}>
        {isLoggedIn && <MobileNav />}
        <main className="flex-1 mobile-container pb-28 md:pb-8 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card-3d p-8 max-w-md w-full text-center"
          >
            <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Send size={40} className="text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-4">Message Sent!</h1>
            <p className="text-slate-600 mb-6">
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <button
              onClick={() => navigate(backTo)}
              className="btn-3d btn-3d-primary h-14 px-8 rounded-2xl font-semibold"
            >
              {isLoggedIn ? 'Back to Settings' : 'Back to Login'}
            </button>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{
      background: 'linear-gradient(135deg, #EFF6FF 0%, #ECFDF5 100%)'
    }}>
      {isLoggedIn && <MobileNav />}

      <main className="flex-1 mobile-container pb-28 md:pb-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(backTo)}
            className="btn-3d h-12 w-12 rounded-xl flex items-center justify-center bg-white border-2 border-slate-200 hover:border-blue-300"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-4xl md:text-5xl font-bold gradient-text">Contact Us</h1>
            <p className="text-slate-600 font-medium">We'd love to hear from you</p>
          </div>
        </div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-3d p-6 md:p-8 max-w-2xl mx-auto"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
              <MessageSquare size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Get in Touch</h2>
              <p className="text-sm text-slate-500">Fill out the form below</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                <User size={16} />
                Your Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="input-3d w-full h-14"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                <Mail size={16} />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@company.com"
                className="input-3d w-full h-14"
                required
              />
            </div>

            {/* Subject */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                <FileText size={16} />
                Subject
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="How can we help?"
                className="input-3d w-full h-14"
                required
              />
            </div>

            {/* Message */}
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2">
                <MessageSquare size={16} />
                Message
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us more about your inquiry..."
                rows={5}
                className="input-3d w-full resize-none"
                style={{ paddingTop: '14px', paddingBottom: '14px' }}
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={sending}
              className="btn-3d btn-3d-primary w-full h-14 rounded-2xl font-semibold flex items-center justify-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Message
                </>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Or email us directly at{' '}
            <a href="mailto:hello@guestcheckoutapp.com" className="text-blue-600 hover:underline font-medium">
              hello@guestcheckoutapp.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
}
